/**
 * @file The Metrics series model of a `bookedSeries`, `ordersWrittenSeries`
 * or `quotesIssuedSeries` answer: one line per served row in rank order, each
 * bucket's start read from the served `bucketStarts` as its local date in
 * `timeZone`, beside its decimal value and count. Consumers: the Metrics tab
 * and `metricsSeriesFigure`. Rank 11 is "All others", a null key at ranks 1 to
 * 10 is "Unassigned", and `nameOf` names every other key. `total` is the
 * served period total, which for a count series can be less than the sum of
 * its buckets, so nothing here sums buckets. One unreadable row fails the
 * whole answer.
 */
import { DateTime } from 'luxon';
import type { Snapshot } from 'valtio';

import { analyticsDecimalFraction } from '#types';
import type { AnalyticsSeriesGroup, NamedQueryCell } from '#types';

import { analyticsMalformedAnswer } from './analyticsMalformedAnswer';
import { analyticsRefusedColumn } from './analyticsRefusedColumn';
import { analyticsUnansweredFigure } from './analyticsUnansweredFigure';
import type {
  AnalyticsSeries,
  AnalyticsSeriesBucket,
  AnalyticsSeriesLine,
} from './analyticsMetricsTab.types';
import type { AnalyticsAnswer } from './index.types';

type Row = Snapshot<Record<string, NamedQueryCell>>;

const ALL_OTHERS_RANK = 11;

const ALL_LINE = 'Across all entities';

const SERIES_COLUMNS = ['values', 'counts', 'total'];

const isDecimal = (cell: unknown): cell is string =>
  typeof cell === 'string' && analyticsDecimalFraction(cell) !== undefined;

const jsonList = (cell: NamedQueryCell | undefined): unknown[] | null => {
  if (typeof cell !== 'string') return null;
  try {
    const parsed: unknown = JSON.parse(cell);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const localDate = (start: unknown, timeZone: string): string | null =>
  typeof start === 'string'
    ? DateTime.fromSQL(start, { zone: timeZone }).toISODate()
    : null;

const bucketsOf = (
  row: Row,
  timeZone: string,
): AnalyticsSeriesBucket[] | null => {
  const starts = jsonList(row.bucketStarts);
  const values = jsonList(row.values);
  const counts = jsonList(row.counts);
  if (starts === null || values === null || counts === null) return null;
  if (values.length !== starts.length || counts.length !== starts.length)
    return null;
  const buckets = starts.flatMap((start, index) => {
    const date = localDate(start, timeZone);
    const value = values[index];
    const count = counts[index];
    return date !== null && isDecimal(value) && Number.isInteger(count)
      ? [{ start: date, value, count: Number(count) }]
      : [];
  });
  return buckets.length === starts.length ? buckets : null;
};

const seriesLine = (
  row: Row,
  group: AnalyticsSeriesGroup,
  timeZone: string,
  nameOf: (groupKey: string) => string,
): AnalyticsSeriesLine | null => {
  const { groupKey, groupRank } = row;
  const total = typeof row.total === 'number' ? String(row.total) : row.total;
  const buckets = bucketsOf(row, timeZone);
  if (typeof groupRank !== 'number' || !Number.isInteger(groupRank))
    return null;
  if (typeof groupKey !== 'string' && groupKey !== null) return null;
  if (!isDecimal(total) || buckets === null) return null;
  const name =
    groupRank === ALL_OTHERS_RANK
      ? 'All others'
      : group === 'all'
        ? ALL_LINE
        : groupKey === null
          ? 'Unassigned'
          : nameOf(groupKey);
  return { rank: groupRank, name, total, buckets };
};

export const analyticsSeriesRows = (
  answer: Snapshot<AnalyticsAnswer> | undefined,
  timeZone: string,
  nameOf: (groupKey: string) => string,
): AnalyticsSeries => {
  if (answer?.status !== 'answered') return analyticsUnansweredFigure(answer);
  const { result } = answer;
  const refused = SERIES_COLUMNS.map((column) =>
    analyticsRefusedColumn(result, column),
  ).find((column) => column !== null);
  if (refused !== undefined)
    return { status: 'refusedColumn', column: refused };
  const { grain, group } = result.parameters;
  const totalGroups = result.rows[0]?.totalGroups ?? 0;
  const lines = result.rows.flatMap((row) => {
    const line =
      group === undefined ? null : seriesLine(row, group, timeZone, nameOf);
    return line === null || row.totalGroups !== totalGroups ? [] : [line];
  });
  if (
    grain === undefined ||
    group === undefined ||
    typeof totalGroups !== 'number' ||
    lines.length !== result.rows.length
  )
    return analyticsMalformedAnswer(result.query);
  return {
    status: 'answered',
    grain,
    group,
    totalGroups,
    lines: lines.sort((a, b) => a.rank - b.rank),
  };
};
